import React from 'react';
import { Box, Typography, Card, CardContent, Grid, Chip, Divider } from '@mui/material';
import { Github, Globe, Mail, Heart } from 'lucide-react';

export default function AboutView() {
  return (
    <Box
      sx={{
        height: 'calc(100vh - 90px)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto',
        px: 3,
        py: 2,
      }}
    >
      {/* Header */}
      <Typography variant="h5" fontWeight={700} mb={3}>
        About Project Manager
      </Typography>

      {/* Main Info Card */}
      <Card variant="outlined" sx={{ mb: 3, maxWidth: 700 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={700} mb={1}>
            Project Manager v1.0.0
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            A comprehensive project management application designed to help teams organize, track, and
            deliver projects efficiently. Built with modern web technologies for optimal performance and
            user experience.
          </Typography>

          <Divider sx={{ my: 2 }} />

          {/* Features */}
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            Key Features
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
            {[
              'Dashboard Analytics',
              'Project Tracking',
              'Task Management',
              'Team Collaboration',
              'Budget Monitoring',
              'Timeline Visualization',
              'Kanban Board',
              'AI-Powered Reports',
            ].map((feature) => (
              <Chip key={feature} label={feature} size="small" variant="outlined" />
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Tech Stack */}
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            Built With
          </Typography>
          <Grid container spacing={2}>
            {[
              { label: 'React 18', desc: 'Frontend framework' },
              { label: 'TypeScript', desc: 'Type safety' },
              { label: 'Material-UI v7', desc: 'Component library' },
              { label: 'Cloudflare Pages', desc: 'Hosting & deployment' },
              { label: 'Workers AI', desc: 'AI-powered features' },
              { label: 'Recharts', desc: 'Data visualization' },
            ].map((tech, i) => (
              <Grid size={{ xs: 12, sm: 6 }} key={i}>
                <Box>
                  <Typography variant="caption" fontWeight={700} color="primary">
                    {tech.label}
                  </Typography>
                  <Typography variant="caption" display="block" color="text.secondary">
                    {tech.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Contact & Links */}
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            Support & Links
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Mail size={16} />
              <Typography variant="body2">
                Questions? Contact us at{' '}
                <Typography component="span" fontWeight={600}>
                  support@projectmanager.local
                </Typography>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Heart size={16} color="#ef4444" />
              <Typography variant="body2">
                Built with care by the Project Manager team
              </Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Acknowledgments */}
      <Card variant="outlined" sx={{ maxWidth: 700 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="subtitle2" fontWeight={700} mb={1.5}>
            Acknowledgments
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            We&apos;d like to thank our team and all contributors who made this project possible. Special
            thanks to the open-source community for the amazing libraries and tools we use.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            © 2026 Project Manager. All rights reserved.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}
